import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./styles.scss";
import {
  HomeProfileSection,
  HomeCategorySection,
  AddItemPopup,
  TranslatePopup,
  InfoCard,
  AddDocumentPopup,
  GenericModal,
  CardOrderWizard,
  ShareMedicalModal,
  MedicalNotesCard,
} from "components";
import {
  isPersonalInfoComplete,
  isAddressComplete,
  isInsuranceComplete,
  isEmergencyComplete,
} from "components/card-order-wizard";
import { CATEGORIES_TYPES, ENUMS, constants } from "common";
import { images } from "common/images";
import { getDashboardStats } from "services";
import {
  deleteItem,
  fetchAllBusinesses,
  getAllProfileRecords,
  getDashboardDocumentStats,
  saveUserData,
  updateUserData,
  updateUserReOrderData,
} from "services/firebaseServices";
import { ApplicationContext } from "shared";
import {
  getProfile,
  getSecondaryProfile,
  setSecondaryProfile,
} from "utilities";
import axios from "axios";
import Stripe from "stripe";
import { useSearchParams } from "react-router-dom";
import { selectedStripeKey } from "common/constants";
import { MedicalCard } from "../../components/medical-card";
import { ShortMedicalCard } from "components/medical-card/short-medical-card";
import { CircularProgress } from "@mui/material";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

export const formatDate = (date) => {
  const options = { day: "2-digit", month: "2-digit", year: "numeric" };
  const formattedDateString = date?.toLocaleDateString("en-GB", options);
  return formattedDateString;
};

const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cardOrderBtn, setCardOrderBtn] = useState(false);
  const [cardReorderBtn, setCardReOrderBtn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { categoryListing, setCategoryListing, userData, setUserData } =
    useContext(ApplicationContext);
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [showTranslate, setShowTranslate] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { cardWithLock } = images;
  const [showModal, setShowModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showReorderConfirmationModal, setShowReOrderConfirmationModal] =
    useState(false);
  const [itemDescription, setItemDescription] = useState({
    type: "",
    description: "",
  });

  const [showCardWizard, setShowCardWizard] = useState(false);
  const [wizardOrderMode, setWizardOrderMode] = useState("order"); // 'order' | 'reorder'
  const wizardCheckDone = useRef(false);

  const [apiResponse, setApiResponse] = useState({
    type: ENUMS.MODAL_TYPES.ERROR,
    title: "",
    body: "",
  });

  useEffect(() => {
    fetchRecords(true);
    const profileData = getSecondaryProfile();
    setUserData(profileData);

    // Show welcome modal only when all required data is complete
    if (
      localStorage.getItem("welcome") &&
      isPersonalInfoComplete(profileData) &&
      isAddressComplete(profileData) &&
      isEmergencyComplete(profileData)
    ) {
      setShowWelcomeModal(true);
    }

    // Wizard check is deferred to the userData useEffect below,
    // so it only runs once Firebase data has loaded (avoiding a race condition
    // where localStorage has no cardOrderStatus yet but Firebase does).
  }, []);

  // Run the wizard check exactly once, but only after Firebase has populated
  // userData (detected by the object having keys). This prevents the wizard
  // from opening for users who have already ordered their card but whose
  // localStorage was empty on this session.
  useEffect(() => {
    if (wizardCheckDone.current) return;
    if (!userData || Object.keys(userData).length === 0) return; // wait for Firebase data
    wizardCheckDone.current = true;
    checkAndShowRequiredModals(userData);
  }, [userData]);

  // Safety net: once the authoritative profile shows the card is ordered,
  // close the wizard even if a stale localStorage pass opened it first. This
  // prevents a fully-registered user from being trapped in the order wizard
  // after a full reload (e.g. following a deploy).
  useEffect(() => {
    if (userData?.cardOrderStatus === "Registered") {
      setShowCardWizard(false);
    }
  }, [userData?.cardOrderStatus]);

  // Open the unified wizard whenever any required step is incomplete or wizard was pending,
  // but only if the card has not already been ordered
  const checkAndShowRequiredModals = (profileData) => {
    // Card already ordered — nothing to do
    if (profileData?.cardOrderStatus === "Registered") return;

    const anyIncomplete =
      !isPersonalInfoComplete(profileData) ||
      !isAddressComplete(profileData) ||
      !isEmergencyComplete(profileData);

    // Open wizard if any profile step is incomplete, wizard was pending, or card not yet ordered
    if (
      anyIncomplete ||
      localStorage.getItem("wizardPending") ||
      !profileData?.cardOrderStatus
    ) {
      setWizardOrderMode("order");
      setShowCardWizard(true);
    }
  };

  useEffect(() => {
    const tempData = {
      ...userData,
      cardReOrderStatus: "Registered",
      cardReOrderDate: formatDate(new Date()),
    };
    if (searchParams?.get("reorder_status")) {
      setCardReOrderBtn(true);
      (async () => {
        try {
          // 1. Save reorder data to Firebase first
          const getProfileData = await getProfile();
          if (getProfileData && getProfileData.uid) {
            await updateUserReOrderData(getProfileData.uid, tempData);
            console.log('[Reorder] Data saved to Firebase');

            // 2. Generate PDF via same-origin proxy (bypasses CORS/CSP restrictions)
            console.log('[Reorder] Triggering PDF generation for user:', getProfileData.uid);
            const pdfRes = await fetch(`/api/generate-pdf?userId=${getProfileData.uid}`);
            const pdfData = await pdfRes.json();
            console.log('[Reorder] PDF generation result:', pdfData);
          } else {
            console.error('[Reorder] No user profile or uid found');
          }
        } catch (error) {
          console.error('[Reorder] Error:', error.message);
        }

        // 4. Update UI after completion
        searchParams.delete("reorder_status");
        setSearchParams(searchParams);
        setCardReOrderBtn(false);
        setApiResponse({
          type: ENUMS.MODAL_TYPES.SUCCESS,
          title: "Success",
          body: "Your Card has been ReOrdered successfully",
        });
        setShowModal(true);
      })();
    }
  }, [searchParams]);

  const shortCardData = useMemo(() => {
    return {
      diagnosis: (
        categoryListing
          .find((item) => item.type === CATEGORIES_TYPES.diagnoses)
          ?.dataListing?.map((i) => i.description) || []
      ).join(", "),
      medication: (
        categoryListing
          .find((item) => item.type === CATEGORIES_TYPES.medicines)
          ?.dataListing?.map((i) => i.description) || []
      ).join(", "),
      allergies: (
        categoryListing
          .find((item) => item.type === CATEGORIES_TYPES.allergies)
          ?.dataListing?.map((i) => i.description) || []
      ).join(", "),
      vaccines: (
        categoryListing
          .find((item) => item.type === CATEGORIES_TYPES.vaccines)
          ?.dataListing?.map((i) => i.description) || []
      ).join(", "),
    };
  }, [categoryListing]);

  const fetchRecords = async (isToFetchAll) => {
    try {
      setIsLoading(true);
      const tempData = [...categoryListing];
      if (isToFetchAll) {
        tempData[0].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.diagnoses,
        );
        tempData[1].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.medicines,
        );
        tempData[2].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.allergies,
        );
        tempData[3].dataListing = await getDashboardStats(
          CATEGORIES_TYPES.vaccines,
        );
        tempData[4].dataListing = await getDashboardDocumentStats(
          CATEGORIES_TYPES.documents,
        );
      } else {
        let selectedIndex = tempData.findIndex(
          (item) => item.type == selectedType,
        );
        if (selectedIndex == 4) {
          tempData[selectedIndex].dataListing =
            await getDashboardDocumentStats(selectedType);
        } else {
          tempData[selectedIndex].dataListing =
            await getDashboardStats(selectedType);
        }
      }
      setIsLoading(false);
      setCategoryListing(tempData);
    } catch (err) {
      console.log(err);
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setShowDocuments(true);
  };

  const getTheUpdatedList = (currentType) => {
    setIsLoading(true);
    let dataString = `These are my ${currentType} => `;
    const currentDataListing = categoryListing.filter(
      (item) => item.type == currentType,
    );
    currentDataListing[0]?.dataListing?.map((item, index) => {
      dataString = dataString.concat(`${index + 1} ${item?.description} \n`);
    });
    setIsLoading(false);
    return dataString;
  };

  const [reorderPromo, setReorderPromo] = useState("");
  const [reorderPromoError, setReorderPromoError] = useState("");

  const fulfillFreeReorder = async () => {
    const tempData = {
      ...userData,
      cardReOrderStatus: "Registered",
      cardReOrderDate: formatDate(new Date()),
    };
    try {
      const getProfileData = await getProfile();
      if (getProfileData && getProfileData.uid) {
        await updateUserReOrderData(getProfileData.uid, tempData);
        const pdfRes = await fetch(`/api/generate-pdf?userId=${getProfileData.uid}`);
        await pdfRes.json();
      }
    } catch (e) {
      console.error("[Reorder free] Error:", e.message);
    }
    setReorderPromo("");
    setShowReOrderConfirmationModal(false);
    setCardReOrderBtn(false);
    setApiResponse({
      type: ENUMS.MODAL_TYPES.SUCCESS,
      title: "Success",
      body: "Your Card has been ReOrdered successfully",
    });
    setShowModal(true);
  };

  const reOrderCardWithStripe = async () => {
    setCardReOrderBtn(true);
    console.log("reorder", formatDate(new Date()));
    setSecondaryProfile({
      ...userData,
      cardReOrderStatus: "Registered",
      cardReOrderDate: formatDate(new Date()),
    });
    let selectedCurr = "GBP";
    let amount = 499;
    try {
      const result = await fetch("/api/exchange-rates?base=GBP");
      const currencies = await result.json();

      const countryToCurrency = {
        US: "USD",
        GB: "GBP",
        PK: "PKR",
        IN: "INR",
        AU: "AUD",
        CA: "CAD",
        JP: "JPY",
        CN: "CNY",
        BR: "BRL",
        MX: "MXN",
        RU: "RUB",
        KR: "KRW",
        CH: "CHF",
        SE: "SEK",
        NO: "NOK",
        DK: "DKK",
        PL: "PLN",
        TR: "TRY",
        ZA: "ZAR",
        NZ: "NZD",
        SG: "SGD",
        HK: "HKD",
        MY: "MYR",
        ID: "IDR",
        TH: "THB",
        PH: "PHP",
        VN: "VND",
        EG: "EGP",
        SA: "SAR",
        AE: "AED",
        NG: "NGN",
        KE: "KES",
        GH: "GHS",
        DE: "EUR",
        FR: "EUR",
        IT: "EUR",
        ES: "EUR",
        NL: "EUR",
        BE: "EUR",
        PT: "EUR",
        AT: "EUR",
        FI: "EUR",
        IE: "EUR",
        GR: "EUR",
        LU: "EUR",
      };
      let detectedCurr = "GBP";
      try {
        const geoRes = await fetch("https://ipinfo.io/json");
        if (geoRes.ok) {
          const geo = await geoRes.json();
          const country = (geo.country || "").trim().toUpperCase();
          detectedCurr = countryToCurrency[country] || "GBP";
        }
      } catch {
        // Fallback: detect country from browser timezone
        const tzCountry = {
          "Europe/Madrid":"ES","Europe/London":"GB","Europe/Berlin":"DE",
          "Europe/Paris":"FR","Europe/Rome":"IT","Europe/Amsterdam":"NL",
          "Europe/Brussels":"BE","Europe/Lisbon":"PT","Europe/Vienna":"AT",
          "Europe/Helsinki":"FI","Europe/Dublin":"IE","Europe/Athens":"GR",
          "Europe/Luxembourg":"LU","Europe/Stockholm":"SE","Europe/Oslo":"NO",
          "Europe/Copenhagen":"DK","Europe/Warsaw":"PL","Europe/Istanbul":"TR",
          "Europe/Zurich":"CH","America/New_York":"US","America/Chicago":"US",
          "America/Denver":"US","America/Los_Angeles":"US","America/Toronto":"CA",
          "America/Vancouver":"CA","America/Sao_Paulo":"BR","America/Mexico_City":"MX",
          "Asia/Tokyo":"JP","Asia/Shanghai":"CN","Asia/Kolkata":"IN","Asia/Karachi":"PK",
          "Asia/Singapore":"SG","Asia/Hong_Kong":"HK","Asia/Kuala_Lumpur":"MY",
          "Asia/Jakarta":"ID","Asia/Bangkok":"TH","Asia/Manila":"PH","Asia/Seoul":"KR",
          "Asia/Ho_Chi_Minh":"VN","Asia/Dubai":"AE","Asia/Riyadh":"SA",
          "Australia/Sydney":"AU","Pacific/Auckland":"NZ","Africa/Johannesburg":"ZA",
          "Africa/Cairo":"EG","Africa/Lagos":"NG","Africa/Nairobi":"KE","Africa/Accra":"GH",
          "Europe/Moscow":"RU","Europe/Canary":"ES","Atlantic/Canary":"ES",
        };
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const tzCountryCode = tzCountry[tz];
          if (tzCountryCode && countryToCurrency[tzCountryCode]) {
            detectedCurr = countryToCurrency[tzCountryCode];
          }
        } catch {}
        if (detectedCurr === "GBP") {
          const lang = navigator.language || "en-GB";
          const region = (lang.split("-")[1] || "GB").toUpperCase();
          detectedCurr = countryToCurrency[region] || "GBP";
        }
      }

      if (currencies.rates[detectedCurr]) {
        selectedCurr = detectedCurr;
        amount = Math.ceil(currencies.rates[detectedCurr] * 499);
      }
      console.log("wazifa", selectedCurr, amount);
    } catch (err) {
      selectedCurr = "GBP";
      amount = 499;
    }
    // Use server-side Netlify Edge Function for Stripe checkout (bypasses CORS)
    try {
      // Promotion code: validate first. A fully-free (100%) code skips Stripe
      // entirely, because a one-time card payment cannot be EUR 0; partial
      // codes are pre-applied to the Stripe checkout.
      setReorderPromoError("");
      const promoInput = (reorderPromo || "").trim();
      let appliedPromoId = "";
      if (promoInput) {
        let v = null;
        try {
          const ctrl = new AbortController();
          const to = setTimeout(() => ctrl.abort(), 15000);
          const vRes = await fetch('/api/validate-reorder-code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: promoInput, amount: amount + '', currency: selectedCurr, brand: (window.location.hostname.includes('.co.uk') ? 'uk' : 'io') }),
            signal: ctrl.signal,
          });
          clearTimeout(to);
          v = await vRes.json();
        } catch (verr) {
          console.error('[Reorder promo] validate error:', verr);
          setReorderPromoError('Could not check the code. Please try again.');
          setCardReOrderBtn(false);
          return;
        }
        if (!v || !v.valid) {
          setReorderPromoError((v && v.reason) || 'This code is invalid.');
          setCardReOrderBtn(false);
          return;
        }
        if (v.free) {
          await fulfillFreeReorder();
          return;
        }
        appliedPromoId = v.promoId || "";
      }
      const response = await fetch('/api/create-reorder-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currency: selectedCurr,
          amount: amount + '',
          promotionCode: appliedPromoId,
          brand: (window.location.hostname.includes('.co.uk') ? 'uk' : 'io'),
          successUrl: `${constants.siteBaseURL}/?reorder_status=true`,
          cancelUrl: `${constants.siteBaseURL}/`,
        }),
      });
      const data = await response.json();
      if (data?.url) {
        window.location.assign(data.url);
      } else {
        console.error('Reorder checkout error:', data?.error);
        setCardReOrderBtn(false);
      }
    } catch (err) {
      console.error('Error creating reorder checkout session:', err.message);
      setCardReOrderBtn(false);
    }
  };
  const orderCard = async () => {
    setCardOrderBtn(true);
    const tempData = {
      ...userData,
      cardOrderStatus: "Registered",
      cardOrderDate: formatDate(new Date()),
    };
    setSecondaryProfile(tempData);

    try {
      // 1. Save data to Firebase FIRST (direct call, no UI side effects)
      const getProfileData = await getProfile();
      if (getProfileData && getProfileData.uid) {
        await updateUserData(getProfileData.uid, tempData);
        console.log('[OrderCard] Data saved to Firebase');

        // 2. Generate PDF via same-origin proxy (bypasses CORS/CSP restrictions)
        console.log('[OrderCard] Triggering PDF generation for user:', getProfileData.uid);
        const pdfRes = await fetch(`/api/generate-pdf?userId=${getProfileData.uid}`);
        const pdfData = await pdfRes.json();
        console.log('[OrderCard] PDF generation result:', pdfData);
      } else {
        console.error('[OrderCard] No user profile or uid found');
      }
    } catch (error) {
      console.error('[OrderCard] Error:', error.message);
    }

    // 4. AFTER everything completes, update UI
    localStorage.removeItem("wizardPending");
    setShowCardWizard(false);
    setCardOrderBtn(false);
    setApiResponse({
      type: ENUMS.MODAL_TYPES.SUCCESS,
      title: "Success",
      body: "Your Card has been Ordered successfully",
    });
    setShowModal(true);
  };
  const saveDataToFirebase = async (tempData, reOrder) => {
    const getProfileData = await getProfile();
    if (getProfileData) {
      let response;
      if (reOrder) {
        console.log("reorder saveDataToFirebase", tempData);
        response = await updateUserReOrderData(getProfileData?.uid, tempData);
      } else {
        response = await updateUserData(getProfileData?.uid, tempData);
      }
      searchParams.delete("reorder_status");
      setSearchParams(searchParams);
      setCardOrderBtn(false);
      setCardReOrderBtn(false);
      if (response) {
        localStorage.removeItem("wizardPending");
        setShowCardWizard(false);
        let msg = "Ordered";
        if (searchParams?.get("reorder_status")) {
          msg = "ReOrdered";
        }
        setApiResponse({
          type: ENUMS.MODAL_TYPES.SUCCESS,
          title: "Success",
          body: `Your Card hass been ${msg} successfully`,
        });
        setShowModal(true);
      }
    }
  };

  const [businessData, setBusinessData] = useState(null);
  const [reorderEligible, setReorderEligible] = useState(false);

  const fetchBusinessInfo = async () => {
    console.log("Current profile:====> inside");

    try {
      const profile = getProfile();
      console.log("Current profile:====>", profile);
      if (!profile?.uid) return;

      const db = getFirestore();

      // 1️⃣ Check if user has a business-linked signup
      const signupLink = profile?.signupLink || window.location.href;
      const urlSegments = signupLink.split("/"); // ['', 'business-name', 'sign-up']
      const businessNameFromLink = urlSegments[1];
      console.log("Current profile:====> signupLink", signupLink);

      if (businessNameFromLink && businessNameFromLink !== "sign-up") {
        // 2️⃣ Fetch the business by name
        const businessRef = collection(db, "businesses");
        const q = query(
          businessRef,
          where("businessName", "==", businessNameFromLink),
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const linkedBusiness = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setBusinessData(linkedBusiness[0]); // store first business
          setReorderEligible(true); // mark reorder as eligible
          console.log("Linked business data:", linkedBusiness[0]);
        } else {
          console.log(
            "Linked business data ======No business found for this signup link",
          );
          setReorderEligible(false);
        }
      } else {
        // 3️⃣ Normal user or business user
        const businessRef = collection(db, "businesses");
        const q = query(businessRef, where("userId", "==", profile.uid));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          setBusinessData(snapshot.docs[0].data());
        }

        setReorderEligible(false); // reorder button not free
      }
    } catch (error) {
      console.error("Error fetching business info:", error);
    }
  };

  useEffect(() => {
    fetchBusinessInfo();
  }, []);

  useEffect(() => {
    const loadBusinesses = async () => {
      const businesses = await fetchAllBusinesses();
      // Do something with the list
      console.log("all business ===>>>>>", businesses);
    };

    loadBusinesses();
  }, []);

  const [userExist, setUserExist] = useState();

  useEffect(() => {
    async function checkCompanyMatch() {
      const company = userData?.company;
      if (!company || company.trim() === "") {
        setUserExist(false);
        return;
      }
      const businesses = await fetchAllBusinesses();
      const match = businesses.some(
        (b) =>
          b.companyName?.toLowerCase().trim() === company.toLowerCase().trim(),
      );
      setUserExist(match);
    }

    checkCompanyMatch();
  }, [userData]);

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1980,
        margin: "auto",
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 80,
        paddingBottom: 80,
      }}
    >
      <HomeProfileSection userData={userData} />
      {categoryListing.map(
        ({ mainTitle, type, background, icon, dataListing }, index) => {
          return (
            <HomeCategorySection
              key={index}
              isLoading={isLoading}
              mainTitle={mainTitle}
              type={type}
              selectedType={selectedType}
              setSelectedType={(type) => setSelectedType(type)}
              icon={icon}
              background={background}
              dataListing={dataListing}
              refreshStats={() => {
                fetchRecords(false);
              }}
              setApiResponse={setApiResponse}
              setShowConfirmationModal={setShowConfirmationModal}
              setItemDescription={setItemDescription}
              onAddButtonClicked={() => {
                if (type !== CATEGORIES_TYPES.documents) {
                  setShowAdd(true);
                } else {
                  openFilePicker();
                  // setShowDocuments(true)
                }
                setSelectedType(type);
              }}
              onTranslateButtonClicked={() => {
                setShowTranslate(true);
                setSelectedType(type);
              }}
              onShareButtonClicked={() => {
                setSelectedType(type);
                setShowShareModal(true);
              }}
            />
          );
        },
      )}

      <MedicalNotesCard
        initialValue={userData?.medicalNotes || ""}
        onSaved={(notes) => setUserData({ ...userData, medicalNotes: notes })}
      />

      <div className="cardSectionContainer">
        {
          // userData?.cardOrderStatus == "" ? (
          //   <div className="card-cont">
          //     <div className="cardContainer">
          //       <img
          //         src={cardWithLock}
          //         height="auto"
          //         width="100%"
          //         alt="no-image"
          //       />
          //     </div>
          //   </div>
          // ) : (
          // <div className="cardContainer-bg">
          //   <div className="card-with-lock">
          //     <div>
          //       <span className="card-without-lock-item-title">{"Name"}</span>
          //       <p className="card-name">
          //         {`${userData?.firstName} ${userData?.lastName}`}
          //       </p>
          //       <div className="card-without-item-cont">
          //         <CardWithoutLockItem
          //           title={"National ID No."}
          //           value={userData?.ssn}
          //         />
          //         <CardWithoutLockItem
          //           title={"Date of Birth"}
          //           value={`${new Date(
          //             userData.birthDate
          //           ).toLocaleDateString()}`}
          //         />
          //         <CardWithoutLockItem
          //           title={"Issued on"}
          //           value={`${new Date(
          //             userData.cardOrderDate
          //           ).toLocaleDateString()}`}
          //         />
          //       </div>
          //     </div>
          //   </div>
          // </div>
          <div className="home-card">
            <MedicalCard
              {...userData}
              emergencyNumber={userData?.emergencyContact?.[0]?.phone}
              policyNumber={userData?.insurance?.[0]?.policy}
              insuranceCompany={userData?.insurance?.[0]?.company}
            />
          </div>

          // )
        }
        <GenericModal
          show={showReorderConfirmationModal}
          type={6}
          title={"Preview card details"}
          body={
            <div>
              <MedicalCard
                {...userData}
                emergencyNumber={userData?.emergencyContact?.[0]?.phone}
                policyNumber={userData?.insurance?.[0]?.policy}
                insuranceCompany={userData?.insurance?.[0]?.company}
              />
              <div style={{ width: "100%", maxWidth: 320, margin: "16px auto 0", boxSizing: "border-box", textAlign: "center" }}>
                <input
                  type="text"
                  value={reorderPromo}
                  onChange={(e) => { setReorderPromo(e.target.value); setReorderPromoError(""); }}
                  placeholder="Promotion code (optional)"
                  style={{ width: "100%", boxSizing: "border-box", padding: "10px 14px", borderRadius: 24, border: "1px solid #ccc", fontSize: 14, outline: "none" }}
                />
                {reorderPromoError ? (
                  <div style={{ color: "#c00", fontSize: 13, marginTop: 6 }}>{reorderPromoError}</div>
                ) : null}
              </div>
            </div>
          }
          buttonText={
            cardReorderBtn ? (
              <CircularProgress size={18} color={"inherit"} />
            ) : (
              "⁠Proceed with order"
            )
          }
          button2
          buttonText2={constants.cancel}
          primaryBtnPress={reOrderCardWithStripe}
          secondaryBtnPress={() => { setReorderPromo(""); setReorderPromoError(""); setShowReOrderConfirmationModal(false); }}
        />
        <div className="cardContainer">
          <InfoCard
            loading={cardOrderBtn}
            userData={userData}
            payable={false}
            onSharePress={() => setShowShareModal(true)}
            onOrderPress={() => {
              setWizardOrderMode("order");
              setShowCardWizard(true);
            }}
          />
        </div>

        <ShareMedicalModal
          open={showShareModal}
          onClose={() => setShowShareModal(false)}
          userData={userData}
        />
        <div className="cardContainer">
          <InfoCard
            userExist={userExist}
            loading={false}
            userData={userData}
            payable={true}
            onReOrderPress={() => {
              setShowReOrderConfirmationModal(true);
            }}
            // onReOrderPress={() => {
            //   setWizardOrderMode('reorder');
            //   setShowCardWizard(true);
            // }}
          />
        </div>
      </div>

      {showAdd && (
        <AddItemPopup
          showAdd={showAdd}
          setShowAdd={setShowAdd}
          type={selectedType}
          refreshStats={() => {
            fetchRecords(false);
          }}
          mainTitle={selectedType}
        />
      )}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {showDocuments && (
        <AddDocumentPopup
          showAdd={true}
          setShowAdd={setShowDocuments}
          type={selectedType}
          file={file}
          refreshStats={() => {
            fetchRecords(false);
          }}
          mainTitle={selectedType}
        />
      )}
      {showTranslate && (
        <TranslatePopup
          showAdd={showTranslate}
          setShowAdd={setShowTranslate}
          type={selectedType}
          mainTitle={selectedType}
        />
      )}
      <GenericModal
        show={showModal}
        type={apiResponse.type}
        title={apiResponse.title}
        body={apiResponse.body}
        buttonText={constants.ok}
        handleClose={() => {
          setShowModal(false);
          window.location.reload();
        }}
      />
      {showWelcomeModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '16px', padding: '32px 24px',
            maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <img src={images.success} alt="welcome" style={{ width: '64px', marginBottom: '16px' }} />
            <h4 style={{ fontWeight: 'bold', marginBottom: '12px', color: '#324148' }}>
              Thank you for becoming a member at World Medical Card!
            </h4>
            <p style={{ color: '#4E646E', marginBottom: '24px', fontSize: '15px' }}>
              To get started, remember to complete your medical profile and download the app so you can access all our services anytime.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <a
                href="https://play.google.com/store/apps/details?id=no.wmc&hl=ca&pli=1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={images.playStoreLogo} alt="Download on Google Play" style={{ height: '44px' }} />
              </a>
              <a
                href="https://apps.apple.com/es/app/world-medical-card/id1000435682?l=en-GB"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={images.appStoreLogo} alt="Download on App Store" style={{ height: '44px' }} />
              </a>
            </div>
            <button
              onClick={() => {
                setShowWelcomeModal(false);
                localStorage.removeItem("welcome");
              }}
              style={{
                backgroundColor: '#47B6F6', color: '#fff', border: 'none',
                borderRadius: '8px', padding: '12px 32px', fontSize: '15px',
                fontWeight: 'bold', cursor: 'pointer', width: '100%',
              }}
            >
              Get Started
            </button>
          </div>
        </div>
      )}
      <GenericModal
        show={showConfirmationModal}
        type={apiResponse.type}
        title={apiResponse.title}
        body={apiResponse.body}
        buttonText={constants.ok}
        button2
        buttonText2={constants.cancel}
        primaryBtnPress={async () => {
          setShowConfirmationModal(false);
          await deleteItem(itemDescription.type, itemDescription.description);
          fetchRecords();
        }}
        secondaryBtnPress={() => setShowConfirmationModal(false)}
      />

      <CardOrderWizard
        show={showCardWizard}
        onClose={() => {
          localStorage.removeItem("wizardPending");
          setShowCardWizard(false);
        }}
        categoryListing={categoryListing}
        orderLoading={
          wizardOrderMode === "order" ? cardOrderBtn : cardReorderBtn
        }
        onStepComplete={() => fetchRecords(true)}
        onOrder={() => {
          if (wizardOrderMode === "reorder") {
            reOrderCardWithStripe();
          } else {
            orderCard();
          }
        }}
      />
    </div>
  );
};

const CardWithoutLockItem = ({ title, value }) => {
  return (
    <div>
      <span className="card-without-lock-item-title">{title}</span>
      <p className="card-without-lock-item-value">{value}</p>
    </div>
  );
};

export default Home;
